import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { ArrowLeft, Briefcase, RefreshCw } from 'lucide-react'
import type { Case } from '../../types/case-management'
import {
  CaseSettingsTab,
  CaseInfrastructureTab,
  CaseCollectionsTab,
  CaseChainOfCustodyTab,
  CaseCommunicationsTab,
  CaseProcessingTab,
  CaseScopeTab,
  CaseAnalysisTab,
} from '../case'
import { HistoryTab } from '../case/history/HistoryTab'
import { TABS } from './case-detail-page.tabs'
import {
  DEFAULT_BADGE,
  PRIORITY_BADGES,
  STATUS_BADGES,
  type TabId,
} from './case-detail-page.helpers'

export function LoadingState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <RefreshCw className="h-8 w-8 animate-spin text-[#4e5e82]" />
        <p style={{ color: 'var(--text-muted)' }}>{t('detail.loadingCase')}</p>
      </div>
    </div>
  )
}

export function NotFoundState({
  error,
  t,
}: {
  error: string | null
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
          <Briefcase className="h-10 w-10 text-red-600" />
        </div>
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--text)' }}>
          {t('detail.caseNotFound')}
        </h1>
        <p className="mt-2 max-w-md" style={{ color: 'var(--text-dim)' }}>
          {error ?? t('detail.caseNotFoundDesc')}
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 font-medium text-white transition-colors hover:bg-[#5c6e94]"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('detail.backToDashboard')}
          </Link>
        </div>
      </div>
    </div>
  )
}

interface HeaderProps {
  caseData: Case
  onBack: () => void
  onRefresh: () => void
  t: TFunction<'cases'>
}

export function CaseHeader({ caseData, onBack, onRefresh, t }: HeaderProps): ReactElement {
  return (
    <div className="px-6 py-4">
      <div className="mb-4 flex items-center gap-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 transition-colors"
          style={{ color: 'var(--text-dim)' }}
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">{t('detail.backToCases')}</span>
        </button>
      </div>
      <div className="flex items-start justify-between">
        <CaseTitle caseData={caseData} />
        <CaseBadges caseData={caseData} onRefresh={onRefresh} t={t} />
      </div>
    </div>
  )
}

function CaseTitle({ caseData }: { caseData: Case }): ReactElement {
  return (
    <div className="flex items-start gap-4">
      <div
        className="flex h-12 w-12 items-center justify-center rounded-lg"
        style={{ background: 'rgba(132, 204, 22, 0.12)' }}
      >
        <Briefcase className="h-6 w-6" style={{ color: 'rgb(101, 163, 13)' }} />
      </div>
      <div>
        <h1
          className="text-2xl font-bold"
          style={{ color: 'var(--text)', fontFamily: 'var(--font-display, Syne, sans-serif)' }}
        >
          {caseData.name}
        </h1>
        <CaseSubtitle caseData={caseData} />
      </div>
    </div>
  )
}

function CaseSubtitle({ caseData }: { caseData: Case }): ReactElement {
  return (
    <div className="mt-1 flex items-center gap-3">
      {caseData.orgCaseReference && (
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {caseData.orgCaseReference}
        </span>
      )}
      {caseData.clientName && (
        <>
          <span style={{ color: 'var(--border-hover)' }}>|</span>
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {caseData.clientName}
          </span>
        </>
      )}
    </div>
  )
}

interface BadgesProps {
  caseData: Case
  onRefresh: () => void
  t: TFunction<'cases'>
}

function CaseBadges({ caseData, onRefresh, t }: BadgesProps): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <span
        className="rounded-full px-3 py-1 text-xs font-medium capitalize"
        style={PRIORITY_BADGES[caseData.priority] ?? DEFAULT_BADGE}
      >
        {t(`priority.${caseData.priority}`)}
      </span>
      <span
        className="rounded-full px-3 py-1 text-xs font-medium capitalize"
        style={STATUS_BADGES[caseData.status] ?? DEFAULT_BADGE}
      >
        {t(`caseStatus.${caseData.status}`)}
      </span>
      <button
        onClick={onRefresh}
        className="rounded-lg border p-2 transition-colors"
        style={{ borderColor: 'var(--border-hover)', color: 'var(--text-muted)' }}
        title={t('actions.refresh')}
      >
        <RefreshCw className="h-4 w-4" />
      </button>
    </div>
  )
}

interface TabsNavProps {
  activeTab: TabId
  onChange: (id: TabId) => void
  t: TFunction<'cases'>
}

export function TabsNav({ activeTab, onChange, t }: TabsNavProps): ReactElement {
  return (
    <div className="border-t px-6" style={{ borderColor: 'var(--border)' }}>
      <nav className="-mb-px flex gap-1 overflow-x-auto">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id
          const tabStyle = isActive
            ? { borderColor: 'rgb(132, 204, 22)', color: 'rgb(101, 163, 13)' }
            : { borderColor: 'transparent', color: 'var(--text-muted)' }
          return (
            <button
              key={tab.id}
              onClick={() => {
                onChange(tab.id)
              }}
              className="inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors"
              style={tabStyle}
            >
              {tab.icon}
              {t(`detail.tabs.${tab.id}`)}
            </button>
          )
        })}
      </nav>
    </div>
  )
}

interface OverviewProps {
  caseData: Case
  t: TFunction<'cases'>
}

function OverviewTab({ caseData, t }: OverviewProps): ReactElement {
  return (
    <div className="text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
        <Briefcase className="h-8 w-8 text-slate-400" />
      </div>
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        {t('detail.caseOverview')}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
        {t('detail.overviewDesc')}
      </p>
      <div className="mx-auto mt-6 grid max-w-lg grid-cols-3 gap-4">
        <OverviewStat
          value={caseData.artifactCount.toLocaleString()}
          label={t('detail.artifacts')}
        />
        <OverviewStat
          value={String(caseData.collectionCount)}
          label={t('detail.detailCollections')}
        />
        <OverviewStat
          value={`${String(caseData.analysisCompletion)}%`}
          label={t('detail.analysis')}
        />
      </div>
    </div>
  )
}

function OverviewStat({ value, label }: { value: string; label: string }): ReactElement {
  return (
    <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-800">
      <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  )
}

interface TabContentProps {
  activeTab: TabId
  caseId: string
  caseData: Case
  t: TFunction<'cases'>
}

export function TabContent({ activeTab, caseId, caseData, t }: TabContentProps): ReactElement {
  switch (activeTab) {
    case 'scope':
      return <CaseScopeTab caseId={caseId} />
    case 'settings':
      return <CaseSettingsTab caseId={caseId} />
    case 'infrastructure':
      return <CaseInfrastructureTab caseId={caseId} />
    case 'collections':
      return <CaseCollectionsTab caseId={caseId} />
    case 'processing':
      return <CaseProcessingTab caseId={caseId} />
    case 'analysis':
      return <CaseAnalysisTab caseId={caseId} />
    case 'custody':
      return <CaseChainOfCustodyTab caseId={caseId} />
    case 'communications':
      return <CaseCommunicationsTab caseId={caseId} />
    case 'history':
      return <HistoryTab caseId={caseId} caseName={caseData.name} />
    default:
      return <OverviewTab caseData={caseData} t={t} />
  }
}
